/**
 * The deferred "send the slot proposal" action.
 *
 * Split out of useBoardActions so that file stays under the 500-line limit, and
 * because the interesting part — when the stored case material is downloaded —
 * deserves to be readable on its own.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type { RoleBoard, SchedulingCandidate } from '../../types/taActionBoard';
import type { OfferedEmail } from './OfferedModalBody';
import type { PendingAction } from './usePendingActions';
import { bryqOutcomeText } from '../../services/bryqApi';

export function offerEmailAction({
  candidate,
  role,
  email,
  gmailToken,
  kind = 'case-invite',
  label,
}: {
  candidate: SchedulingCandidate;
  role: RoleBoard;
  email: OfferedEmail;
  gmailToken: string | null;
  /** Which email this is, for the Log page. */
  kind?: 'case-invite' | 'fr-invite';
  /** Overrides the countdown banner's wording (the final round says "final-round"). */
  label?: string;
}): Omit<PendingAction, 'secondsLeft'> {
  return {
    id: `offer:${candidate.opportunityId}`,
    // See PendingAction.affects — an optimistic marker must not outlive the tab.
    affects: [candidate.opportunityId],
    label:
      label ||
      `Sending the slot proposal to ${candidate.name}${
        email.material
          ? ` with ${email.material.kind === 'link' ? email.material.label : email.material.filename}`
          : ''
      }`,
    run: () => {
      // The stored material is downloaded HERE, not at modal-open: a recruiter
      // who never sends should not pay for the transfer.
      // A LINK needs no attachment — it is already in the body text. Only a
      // stored file has bytes to fetch, and that path has never been writable
      // in production (see CaseMaterialRow).
      const attachment =
        email.material && email.material.kind !== 'link'
          ? api.caseMaterialAsAttachment(email.material)
          : Promise.resolve(null);
      attachment
        .then((file) =>
          api.sendCandidateEmail(
            {
              opportunityId: candidate.opportunityId,
              subject: email.subject,
              body: email.body,
              kind,
              roleTitle: role.roleTitle,
              stageName: candidate.stageName,
              attachment: file,
              bryq: email.bryq ?? null,
            },
            gmailToken
          )
        )
        .then((r) =>
          showToast({
            status: 'success',
            description: `Slot proposal sent to ${r.to}${
              r.attachmentName ? ` with ${r.attachmentName}` : ''
            }.${bryqOutcomeText(r.bryq)}`,
          })
        )
        .catch((e) =>
          showToast({
            status: 'error',
            description:
              e instanceof Error && e.message
                ? `The slot proposal was NOT sent: ${e.message}`
                : 'The slot proposal was not sent.',
          })
        );
    },
    undo: () =>
      showToast({
        status: 'info',
        description: `Stopped — no email went to ${candidate.name}. The offer is still recorded.`,
      }),
  };
}
